import React, { useState, useEffect, useCallback } from "react";
import { icons } from "../../helpers/constants";
import { fixDateEvent, fixDateEventNew, getEventIcon } from "../../helpers/helperFunctions";
import { useTranslation } from "react-i18next";
import { useMapToggles } from "../Map/ToggleContext";
import { useSidebarContext } from "../Sidebar/SiderbarContext";
import { motion } from "framer-motion";
import { useIsMobile } from "../../helpers/customHooks";
import EventMap from "../Map/EventMap";

const EventRowNew = ({ event, setSelected, checkbox, selected, currentEvent, setCurrentEvent, devScreen = false, index }) => {
	const { t } = useTranslation();
	const { setActiveEvent } = useMapToggles();
	const { setActionbarContent, setTopbarContent } = useSidebarContext();
	const MOBILE = useIsMobile();

	const [address, setAddress] = useState(null);

	const closeEvent = useCallback(() => {
		setTopbarContent(null);
		setActionbarContent(null);
		setCurrentEvent(null);
		setActiveEvent(null);
		setAddress(null);
	}, [setTopbarContent, setActionbarContent, setCurrentEvent, setActiveEvent]);

	const calculateAddress = useCallback((lat, lng) => {
		if (lat == null || lng == null) return;

		const geocoder = new window.google.maps.Geocoder();
		const latandLng = new window.google.maps.LatLng(lat, lng);

		geocoder.geocode({ location: latandLng }, (results, status) => {
			if (status === "OK" && results?.[0]) {
				setAddress(results[0].formatted_address);
			}
		});
	}, []);

	const additionalCard = useCallback(
		(length, type, info, idx) => {
			let text1;
			let text2;
			let value;

			switch (type) {
				case "deviceOverspeed":
					value = info.value;
					text1 = t("events.speed_limit");
					text2 = t("events.noted_speed");
					break;
				case "geofenceEnter":
				case "geofenceExit":
					value = info.value;
					text1 = t("events.affected_geofence");
					text2 = "";
					break;
				case "deviceWake":
				case "alarmcenter":
					value = info.value;
					text1 = t("events.additional_info");
					text2 = "";
					break;
				case "deviceTempExceeded":
				case "deviceTempBelow":
					value = `${(info.value / 100).toFixed(2)}°`;
					text1 = t("events.temp_limit");
					text2 = t("events.temp_noted");
					break;
				case "deviceHumExceeded":
				case "deviceHumBelow":
					value = `${(info.value / 100).toFixed(2)}`;
					text1 = t("events.hum_limit");
					text2 = t("events.hum_noted");
					break;
				case "test_failed":
				case "test_passed":
					value = `${info.value}`;
					text1 = t("events.per_mille");
					text2 = "";
					break;
				case "temporary_override_activated":
					value = `${info.value}`;
					text1 = t("events.temporary_override");
					text2 = "";
					break;
				default:
					return null;
			}

			return (
				<div key={idx} className={`col-${12 / length} ${length === 1 ? "" : idx === length - 1 ? "ps-2" : "pe-2"} m-0 p-0 pb-3`}>
					<div className="position-relative d-flex bg-white flex-column rounded24 p-3 justify-content-center align-items-center">
						<img src={getEventIcon(type)} alt="" className="size_15 position-absolute top-0 end-0 me-2 mt-2 primary-icon" />
						<h4 className="primary">
							{value} {info.unit && info.unit}
						</h4>
						<p className="thin dark_grey text-truncate">{idx === 0 ? text1 : text2}</p>
					</div>
				</div>
			);
		},
		[t],
	);

	const renderInfo = useCallback(() => {
		const no_pos = event.latitude == null || event.longitude == null;

		return (
			<>
				<div className="row m-0 p-0 mx-3 gx-3">
					<div className="col-8 m-0 p-0 pe-2">
						<div className="bg-white d-flex flex-column rounded24 position-relative p-3 justify-content-center align-items-center overflow-hidden">
							<img src={icons.sidebar.device_active} alt="" className="size_15 position-absolute top-0 end-0 me-2 mt-2 primary-icon" />
							<h4 className="primary text-truncate mx-5 text-center w-75">{event.devicenameorfrom}</h4>
							<p className="thin dark_grey text-truncate">{t("events.device")}</p>
						</div>
					</div>
					<div className="col-4 m-0 p-0 ps-2">
						<div className="bg-white d-flex flex-column rounded24 position-relative p-3 justify-content-center align-items-center">
							<img src={icons.clock} alt="" className="size_15 position-absolute top-0 end-0 me-2 mt-2 primary-icon" />
							<h4 className="primary">{fixDateEvent(event.date, false)}</h4>
							<p className="thin dark_grey text-truncate">{fixDateEvent(event.date)}</p>
						</div>
					</div>
				</div>

				<div className="row m-0 p-0 m-3">
					<div
						className={`col-12 m-0 p-0 bg-white d-flex flex-column rounded24 position-relative p-3 justify-content-center align-items-center ${no_pos ? "" : "cursor-pointer"}`}
						onClick={() => {
							if (no_pos) return;
							// only calculate if we don't have it yet
							if (!address) calculateAddress(event.latitude, event.longitude);
						}}
					>
						<img src={icons.search} alt="" className="size_15 position-absolute top-0 end-0 me-2 mt-2 primary-icon" />

						{no_pos ? (
							<>
								<h4 className="primary">{t("events.no_position")}</h4>
								<p className="thin dark_grey text-truncate">{t("events.additional_info")}</p>
							</>
						) : (
							<>
								<h4 className="primary mx-3">{address ? address : `${event.latitude.toFixed(5)}, ${event.longitude.toFixed(5)}`}</h4>
								<p className="thin dark_grey text-truncate">{t("events.tap_to_calculate")}</p>
							</>
						)}
					</div>
				</div>

				<div className="row m-0 p-0 mx-3">
					{event.additionalinfo &&
						Object.values(event.additionalinfo).map((info, idx) => {
							const len = Object.keys(event.additionalinfo).length;
							return additionalCard(len, event.type, info, idx);
						})}
				</div>
					{!no_pos && MOBILE &&
					<div className="row m-0 p-0 mx-3 mb-3">
						<div className="rounded24 safety-map-border" style={{width: "100%", height: "200px"}}>
								<EventMap alarm={event} />
						</div>
					</div>

					}
			</>
		);
	}, [event, t, address, calculateAddress, additionalCard]);

	// ✅ Key part: whenever currentEvent/address changes, rebuild the sidebar content
	useEffect(() => {
		if (!currentEvent) return;
		if (currentEvent.eventid !== event.eventid) return;

		const actioncontent = (
			<motion.div key="menu" className="flex-grow-1 position-relative zlatan">
				<div className="h-100 overflow-auto">{renderInfo()}</div>
			</motion.div>
		);

		setActionbarContent(actioncontent);
	}, [currentEvent, event.eventid, address, renderInfo, setActionbarContent]);

	const toggleEventBoy = (e) => {
		if (currentEvent?.eventid === e?.eventid) {
			setActiveEvent(null);
			closeEvent();
			return;
		}

		// reset address when opening a different event
		setAddress(null);

		const topcontent = {
			left: (
				<div className="d-flex zlatan">
					<img className="size25 grey-blue-icon me-2" src={getEventIcon(e.type)} alt="" />
					<h3 className="grey-blue">{e.typedesc}</h3>
				</div>
			),
			right: (
				<div className="cursor-pointer pill-button round-bg bg-white menu-button d-flex align-items-center justify-content-center" onClick={closeEvent}>
					<img src={icons.close} alt="back" className="size_15 primary-icon" />
				</div>
			),
		};

		// IMPORTANT: allow lat/lng = 0
		if (e?.latitude != null && e?.longitude != null) {
			setActiveEvent(e);
		} else {
			setActiveEvent(null);
		}

		setCurrentEvent(e);
		setTopbarContent(topcontent);

		// set initial sidebar content immediately
		setActionbarContent(
			<motion.div key="menu" className="flex-grow-1 position-relative zlatan">
				<div className="h-100 overflow-auto">{renderInfo()}</div>
			</motion.div>,
		);
	};

	return (
		<>
			{devScreen ? (
				<div className={`d-flex bg-light-grey border-${index === 0 ? "top" : "bottom"}-12 p-3 mb-1 align-items-center cursor-pointer`} onClick={() => toggleEventBoy(event)}>
					<div className="alarm-img-bg d-flex justify-content-center align-items-center me-3">
						<img className="alarm-img" src={getEventIcon(event.type)} alt="" />
					</div>
					<p className="m-0 p-0 bold">{event.typedesc}</p>
					<div className="d-flex flex-column ms-auto align-items-end">
						<p className="m-0 p-0 bold">{fixDateEventNew(t, event.date)}</p>
						<p className="m-0 p-0 thin dark_grey">{fixDateEvent(event.date, false)}</p>
					</div>
				</div>
			) : (
				<>
					<div className="d-flex p-3 align-items-center cursor-pointer bg-white" onClick={() => (checkbox ? setSelected(event.eventid) : toggleEventBoy(event))}>
						{checkbox ? (
							<div className={`checkbox-${selected ? "active" : "inactive"} rounded8 size_20 d-flex justify-content-center p-1 cursor-pointer me-3`}>{selected && <img className="white-icon" src={icons.check} alt="" />}</div>
						) : (
							<div className="alarm-img-bg d-flex justify-content-center align-items-center me-3">
								<img className="alarm-img" src={getEventIcon(event.type)} alt="" />
							</div>
						)}

						<div className="d-flex flex-column me-3 overflow-hidden">
							<div className="body m-0 p-0 bold text-truncate">{event.typedesc}</div>
							<p className="m-0 p-0 dark-grey">{event.devicenameorfrom}</p>
						</div>

						<div className="d-flex flex-column ms-auto align-items-center">
							<p className="m-0 p-0 bold">{fixDateEventNew(t, event.date)}</p>
							<p className="m-0 p-0 thin dark_grey align-self-end">{fixDateEvent(event.date, false)}</p>
						</div>
					</div>
					<hr className="m-0 p-0 event-divider bg-light-grey" />
				</>
			)}
		</>
	);
};

export default EventRowNew;
